import React from "react";
import {
  Sparkle,
  Instagram,
  MapPin,
  Award,
  Globe2,
  Linkedin,
  Heart,
  Users,
} from "lucide-react";
import { useSiteContent, txt } from "../hooks/useSiteContent";

const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_fd40e688-66bf-4561-91f1-8adae5e464f9/artifacts/oi4gq0c4_111645.jpg";

const stats = [
  { value: "15+", label: "Years in PR & Growth" },
  { value: "300+", label: "Partners closed" },
  { value: "USA · LATAM", label: "Markets covered" },
  { value: "NYC", label: "Home base" },
];

const milestones = [
  {
    icon: Award,
    role: "Head of Influencer Partnerships",
    company: "Hotmart",
    detail:
      "One of the company's top global producers — closed 300+ partners, top launch agencies and creators across South America and the U.S. Hispanic market.",
  },
  {
    icon: Globe2,
    role: "Head of Partnerships · USA & LATAM",
    company: "Nas.com",
    detail:
      "Led creator outreach strategy, recruited agencies, affiliates, partners and influencers, negotiated and closed deals cross-functionally with leadership, marketing, sales and BI.",
  },
  {
    icon: Heart,
    role: "Co-founder",
    company: "TuESTIMA Magazine · Mamás TuESTIMA",
    detail:
      "Platforms created to inspire self-love, emotional well-being, personal growth and self-esteem education for women, families, children, parents and professionals.",
  },
  {
    icon: Users,
    role: "Creator & Campaign Lead",
    company: "LOVE YOURSELF · I LOVE ME / Yo Me AMO",
    detail:
      "Purpose-driven campaigns focused on women's and men's mental health, children's self-esteem, emotional education, and community-based empowerment.",
  },
];

export default function About() {
  const content = useSiteContent();
  const introDefault =
    "Meet Denisse Troconis Aoun — Global Growth, PR, Influencer Marketing & Strategic Partnerships Consultant.";
  const intro = txt(content, "about.intro", introDefault);
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[#050505] py-16 md:py-24 lg:py-32"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Section masthead */}
        <div className="grid grid-cols-12 gap-6 mb-10 md:mb-16 lg:mb-20">
          <div className="col-span-12 md:col-span-3">
            <span className="text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5]">
              ✦ About
            </span>
            <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-white/40">
              Page 04
            </p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2
              data-testid="about-headline"
              className="font-display text-4xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight font-light"
            >
              Built for brands ready to become
              <span className="italic text-[#7FB3D5]"> impossible to ignore.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
          {/* Photo column */}
          <div className="col-span-12 md:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden bg-[#121212]">
              <img
                src={ABOUT_IMG}
                alt="Denisse Troconis Aoun — Founder of NOVA Media PR, editorial portrait"
                data-testid="about-founder-image"
                className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="absolute -top-3 -left-3 w-full h-full border border-[#7FB3D5]/40 -z-0 pointer-events-none" />

            <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4">
              {stats.map((s) => (
                <div
                  key={s.label}
                  data-testid={`about-stat-${s.label}`}
                  className="border-t border-white/10 pt-4"
                >
                  <div className="font-display text-2xl md:text-3xl text-white font-light">
                    {s.value}
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.3em] text-white/50">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Copy column */}
          <div className="col-span-12 md:col-span-7 md:pl-8 lg:pl-16">
            <p className="font-display text-2xl md:text-3xl text-white font-light leading-snug">
              {intro === introDefault ? (
                <>
                  Meet <span className="italic text-[#7FB3D5]">Denisse Troconis Aoun</span> —
                  Global Growth, PR, Influencer Marketing &amp; Strategic
                  Partnerships Consultant.
                </>
              ) : (
                intro
              )}
            </p>

            <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed">
              Denisse has extensive experience helping creators, founders,
              brands and digital businesses grow across the U.S., LATAM and
              Hispanic markets. Her career spans high-growth digital ecosystems
              including <span className="text-white">Hotmart</span>, where she
              served as Head of Influencer Partnerships, and{" "}
              <span className="text-white">Nas.com</span>, where she led
              partnerships across the USA and LATAM.
            </p>

            <p className="mt-5 text-base md:text-lg text-white/70 leading-relaxed">
              Throughout her trajectory, she has built creator outreach
              strategies, negotiated strategic deals, developed business
              partnerships, supported digital product launches, and helped
              brands turn visibility into growth.
            </p>

            {/* Career milestones */}
            <div className="mt-10 space-y-4">
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5]">
                Career highlights
              </p>
              {milestones.map((m) => {
                const Icon = m.icon;
                return (
                  <div
                    key={m.company}
                    data-testid={`about-milestone-${m.company}`}
                    className="flex items-start gap-4 border-l border-[#7FB3D5]/30 pl-5 py-2"
                  >
                    <Icon
                      className="w-4 h-4 text-[#7FB3D5] mt-1 shrink-0"
                      strokeWidth={1.2}
                    />
                    <div>
                      <p className="font-display text-lg md:text-xl text-white font-light leading-tight">
                        {m.role}{" "}
                        <span className="italic text-[#7FB3D5]">— {m.company}</span>
                      </p>
                      <p className="mt-2 text-sm text-white/60 leading-relaxed">
                        {m.detail}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Purpose-driven dimension */}
            <div className="mt-12 border border-[#7FB3D5]/25 bg-[#0a0a0a] p-6 md:p-8 relative">
              <Sparkle
                className="absolute -top-3 -left-3 w-6 h-6 text-[#7FB3D5] bg-[#050505] p-0.5"
                fill="#7FB3D5"
                strokeWidth={0.6}
              />
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5] mb-4">
                ✦ Beyond business · Human-centered purpose
              </p>
              <p className="text-base md:text-lg text-white/75 leading-relaxed font-display italic">
                These initiatives reflect one of Denisse&apos;s deepest
                passions: creating experiences, workshops, and campaigns that
                help people recognize their worth, develop self-respect, and
                build a more conscious relationship with themselves and others.
              </p>
            </div>

            <p className="mt-8 text-base md:text-lg text-white/70 leading-relaxed">
              This unique combination of business strategy, media visibility,
              digital growth, and human-centered purpose is what makes
              Denisse&apos;s approach different. She understands how to build
              brands that are not only{" "}
              <span className="italic text-[#7FB3D5]">seen, but felt</span> —
              brands with a story, a mission, a community, and a clear path to
              growth.
            </p>

            <p className="mt-5 text-base md:text-lg text-white/70 leading-relaxed">
              As a Latina entrepreneur, wife, mother of two, global connector,
              and multi-passionate founder, Denisse brings creativity, empathy,
              strategy, and execution to every project she leads. Her work is
              driven by collaboration, relationship-building, and the belief
              that the right message, placed in the right channels, can create
              both business growth and meaningful impact.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="border-l border-[#7FB3D5]/30 pl-5">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#7FB3D5] mb-2">
                  Approach
                </p>
                <p className="text-sm text-white/70 leading-relaxed italic font-display">
                  The right story, placed in the right media, supported by the
                  right partnerships, can transform how a brand is perceived,
                  trusted, and chosen.
                </p>
              </div>
              <div className="border-l border-[#7FB3D5]/30 pl-5">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#7FB3D5] mb-2">
                  Mission
                </p>
                <p className="text-sm text-white/70 leading-relaxed">
                  To help ambitious brands move from being seen occasionally to
                  being recognized consistently — with the credibility, story,
                  and strategic presence needed to lead in their market.
                </p>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="https://www.linkedin.com/in/denissetroconisaoun"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-linkedin"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-white hover:text-[#7FB3D5] transition-colors duration-300 border-b border-white/20 hover:border-[#7FB3D5] pb-1"
              >
                <Linkedin className="w-3.5 h-3.5" strokeWidth={1.2} />
                LinkedIn
              </a>
              <Sparkle className="w-3 h-3 text-[#7FB3D5]" fill="#7FB3D5" />
              <a
                href="https://instagram.com/denissetroconisaoun"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-instagram-personal"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-white hover:text-[#7FB3D5] transition-colors duration-300 border-b border-white/20 hover:border-[#7FB3D5] pb-1"
              >
                <Instagram className="w-3.5 h-3.5" strokeWidth={1.2} />
                @denissetroconisaoun
              </a>
              <Sparkle className="w-3 h-3 text-[#7FB3D5]" fill="#7FB3D5" />
              <a
                href="https://instagram.com/novamediapr"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-instagram-brand"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-white hover:text-[#7FB3D5] transition-colors duration-300 border-b border-white/20 hover:border-[#7FB3D5] pb-1"
              >
                <Instagram className="w-3.5 h-3.5" strokeWidth={1.2} />
                @novamediapr
              </a>
              <span className="text-white/30">·</span>
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-white/60">
                <MapPin className="w-3.5 h-3.5 text-[#7FB3D5]" strokeWidth={1.2} />
                26 Broadway, New York
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
